import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Eye, EyeOff, Lock, Mail, User, Shield } from 'lucide-react';
import LoadingOverlay from '../components/LoadingOverlay';

const RegistrarFirmante = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [firmanteInfo, setFirmanteInfo] = useState(null);

  useEffect(() => {
    verificarToken();
  }, [token]);

  const verificarToken = async () => {
    try {
      console.log('[REGISTRO] Verificando token:', token);
      const response = await fetch(`${API_BASE_URL}/api/firmar/${token}`);
      
      if (!response.ok) {
        if (response.status === 410) {
          // Documento ya firmado
          toast.error('Este documento ya ha sido firmado. El enlace ha expirado.');
          navigate('/login', { replace: true });
          return;
        }
        console.log('[ERROR] Token no válido, status:', response.status);
        toast.error('Token de firma no válido');
        navigate('/login', { replace: true });
        return;
      }
      
      const data = await response.json();
      console.log('[OK] Datos del firmante:', data);
      
      if (!data.firmante_info) {
        console.log('[ERROR] No se recibió información del firmante');
        toast.error('Error al obtener información del firmante');
        navigate('/login', { replace: true });
        return;
      }

      if (data.usuario_existe === true) {
        toast.info('Ya tienes cuenta. Inicia sesión para firmar el documento.');
        navigate(`/login?token_firma=${token}`, { replace: true });
        return;
      }
      
      setFirmanteInfo(data.firmante_info);
      
      // Prellenar nombre y apellido si están disponibles
      setFormData(prev => ({
        ...prev,
        nombre: data.firmante_info.nombre || '',
        apellido: data.firmante_info.apellido || ''
      }));
      console.log('[OK] Formulario prellenado, mostrando página de registro');
      
    } catch (error) {
      console.error('[ERROR] Error al verificar token:', error);
      toast.error('Error al verificar el token de firma: ' + error.message);
      navigate('/login', { replace: true });
    } finally {
      console.log('[FIN] Finalizando verificación, loading = false');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (!formData.nombre.trim() || !formData.apellido.trim()) {
      toast.error('Nombre y apellido son requeridos');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/registrar-firmante`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token_firma: token,
          nombre: formData.nombre.trim(),
          apellido: formData.apellido.trim(),
          password: formData.password
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Registro exitoso. Redirigiendo al documento...');
        
        // Iniciar sesión automáticamente
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        
        // Redirigir al editor para firmar el documento
        setTimeout(() => {
          navigate(`/editor/${token}`, { replace: true });
        }, 900);
      } else {
        const errorData = await response.json();
        // Si el usuario ya existe, redirigir al login con el token
        if (response.status === 400 && errorData.detail && errorData.detail.includes('Ya existe un usuario')) {
          toast.info('Ya tienes una cuenta. Por favor, inicia sesión para firmar el documento.');
          navigate(`/login?token_firma=${token}`, { replace: true });
        } else {
          toast.error(errorData.detail || 'Error al crear la cuenta');
        }
      }
    } catch (error) {
      console.error('Error al registrar:', error);
      toast.error('Error al crear la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingOverlay message="Verificando información..." />;
  }

  if (!firmanteInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600">No se pudo cargar la información del firmante</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary-600 rounded-full flex items-center justify-center">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Crear tu Cuenta
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Has sido invitado a firmar un documento
          </p>
          {firmanteInfo.email && (
            <p className="mt-1 text-sm text-gray-500">
              Email: <strong>{firmanteInfo.email}</strong>
            </p>
          )}
        </div>

        {/* Banner de "¿Ya tienes cuenta?" */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800 text-center">
            <strong>¿Ya tienes cuenta?</strong> Si ya estás registrado,{' '}
            <a 
              href={`/login?token_firma=${token}`}
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
            >
              inicia sesión aquí
            </a>
          </p>
        </div>

        {/* Formulario de Registro */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-lg">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Nombre */}
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="nombre"
                  name="nombre"
                  type="text"
                  required
                  value={formData.nombre}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="Tu nombre"
                />
              </div>
            </div>

            {/* Apellido */}
            <div>
              <label htmlFor="apellido" className="block text-sm font-medium text-gray-700 mb-2">
                Apellido
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="apellido"
                  name="apellido"
                  type="text"
                  required
                  value={formData.apellido}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="Tu apellido"
                />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-field pl-10 pr-10"
                  placeholder="Mínimo 6 caracteres"
                  minLength={6}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Mínimo 6 caracteres
              </p>
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-field pl-10 pr-10"
                  placeholder="Repite tu contraseña"
                  minLength={6}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Botón de Registro */}
            <div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creando cuenta...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Crear Cuenta y Continuar
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Información */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Importante:</strong> Una vez que crees tu cuenta, podrás firmar el documento que te fue enviado.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Sistema de Firma Electrónica - INDUMA
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrarFirmante;
